import type { FieldErrors } from 'react-hook-form';
import { HISTORICAL_PREFILL_ERROR_TYPE } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/historicalPrefill/constants.ts';

export type TDocumentGenerationValidationIssues = {
  requiredFieldNames: string[];
  invalidFieldNames: string[];
};

type TFieldErrorLike = {
  type?: unknown;
  message?: unknown;
};

const isFieldError = (value: unknown): value is TFieldErrorLike =>
  typeof value === 'object' && value !== null && ('type' in value || 'message' in value);

const getChildPath = (path: string, key: string) => (path ? `${path}.${key}` : key);

const collectErrorFieldNames = (errors: FieldErrors, path = ''): Array<[string, TFieldErrorLike]> =>
  Object.entries(errors).flatMap(([key, value]) => {
    const fieldPath = getChildPath(path, key);

    if (isFieldError(value)) {
      return [[fieldPath, value]];
    }

    if (!value || typeof value !== 'object') {
      return [];
    }

    return collectErrorFieldNames(value as FieldErrors, fieldPath);
  });

const unique = (fieldNames: string[]) => Array.from(new Set(fieldNames));

export const getDocumentGenerationValidationIssues = (
  errors: FieldErrors,
  requiredFieldNames: string[] = []
): TDocumentGenerationValidationIssues => {
  const issues = collectErrorFieldNames(errors);

  return {
    requiredFieldNames: unique([
      ...requiredFieldNames,
      ...issues.filter(([, error]) => error.type === 'required').map(([fieldName]) => fieldName),
    ]),
    invalidFieldNames: unique(
      issues
        .filter(
          ([, error]) => error.type !== 'required' && error.type !== HISTORICAL_PREFILL_ERROR_TYPE
        )
        .map(([fieldName]) => fieldName)
    ),
  };
};

const getElementFieldName = (element: HTMLElement) =>
  element.getAttribute('name') ?? element.dataset.testid ?? '';

export const focusDocumentGenerationField = (fieldName: string) => {
  const elements = Array.from(document.querySelectorAll<HTMLElement>('[name], [data-testid]'));
  const element = elements.find((candidate) => {
    const candidateName = getElementFieldName(candidate);

    return candidateName === fieldName || candidateName.startsWith(`${fieldName}-`);
  });

  if (!element) {
    return;
  }

  element.scrollIntoView({ behavior: 'smooth', block: 'center' });

  window.requestAnimationFrame(() => {
    element.focus({ preventScroll: true });
  });
};
